import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, deepStringFind, inject, SubTopBar, Tooltip } from 'Mri.Web.Common';
import * as React from 'react';
import { BsFillCaretDownFill } from '@react-icons/all-files/bs/BsFillCaretDownFill';
import { BsFillCaretUpFill } from '@react-icons/all-files/bs/BsFillCaretUpFill';
import { BsFillFunnelFill } from '@react-icons/all-files/bs/BsFillFunnelFill';
import { Input } from 'reactstrap';
import { AccountStore, defaultAggregateColumns, defaultTableColumns } from '../Account/AccountStore';
import { AppStore } from '../Core/AppStore';
import { ColumnSelector } from '../DisplayFilters/ColumnSelector';
import { DisplayFilter } from '../DisplayFilters/DisplayFilter';
import { DisplayListViewModel } from '../DisplayFilters/DisplayListViewModel';
import { DisplayViewSelector, DisplayViewTypes } from '../DisplayFilters/DisplayViewSelector';
import {SortSelector} from "../DisplayFilters/SortSelector";
import {DisplaysTableSettings} from "./DisplaysTableSettings";

export interface IDisplayListNav {
    readonly vm: DisplayListViewModel;
    readonly hideInput?: boolean;
    readonly link?: boolean;
}

@autoinject
@observer
export class DisplayListNav extends React.Component<IDisplayListNav> {
    public render() {
        const {
            columns,
            onColumnChange,
            onColumnGroupSelectAll,
            onFilterDropdownToggle,
            selectAllColumnsChecked,
            onSelectAllColumns,
        } = this.props.vm;

        return (
            <SubTopBar className="d-block">
                <DisplayFilter
                    vm={this.props.vm}
                    hideInput={this.hideInput}
                    renderAfter={
                        <div className="ms-auto flex-items-center">
                            <div
                                className={`extra-filters ${
                                    !this.appStore.isMobile || this.showExtraFilters ? 'show' : ''
                                }`}
                            >
                                <div className="border mx-2 rounded d-flex">
                                    <Input
                                        type="text"
                                        placeholder="Search Columns..."
                                        value={this.columnSearchString}
                                        onChange={this.onColumnSearchChange}
                                        className="border-0 d-inline-block focus-0"
                                    />
                                    <div className="d-inline-block">
                                        <small className="px-2">
                                            {this.searchCells.length > 0
                                                ? this.cellSearchIndex + 1
                                                : this.cellSearchIndex}
                                            /{this.searchCells.length}
                                        </small>
                                        <button
                                            className="btn btn-link d-inline-block"
                                            onClick={this.previousSearchCell}
                                            disabled={this.searchCells.length <= 0}
                                        >
                                            <BsFillCaretUpFill />
                                        </button>
                                        <button
                                            className="btn btn-link d-inline-block"
                                            onClick={this.nextSearchCell}
                                            disabled={this.searchCells.length <= 0}
                                        >
                                            <BsFillCaretDownFill />
                                        </button>
                                    </div>
                                </div>

                                {this.props.vm.viewType != DisplayViewTypes.TableLarge &&
                                    this.props.vm.viewType != DisplayViewTypes.TableSmall &&
                                    this.props.vm.viewType != DisplayViewTypes.TableExtraSmall &&
                                    this.props.vm.viewType != DisplayViewTypes.Aggregate && (
                                        <SortSelector
                                            vm={this.props.vm}
                                            className="d-inline-block ms-1"
                                            link={this.appStore.isMobile}
                                        />
                                    )}

                                {(this.props.vm.viewType == DisplayViewTypes.TableLarge ||
                                    this.props.vm.viewType == DisplayViewTypes.TableSmall ||
                                    this.props.vm.viewType == DisplayViewTypes.TableExtraSmall ||
                                    this.props.vm.viewType == DisplayViewTypes.Aggregate) && (
                                    <ColumnSelector
                                        columns={columns}
                                        onToggle={onFilterDropdownToggle}
                                        onColumnGroupSelectAllChange={onColumnGroupSelectAll}
                                        onChange={onColumnChange}
                                        onReset={this.onColumnReset}
                                        selectAllColumnsChecked={selectAllColumnsChecked}
                                        onSelectAllColumns={onSelectAllColumns}
                                        aggregateView={this.props.vm.viewType == DisplayViewTypes.Aggregate}
                                        className="d-inline-block ms-1"
                                        link={this.appStore.isMobile}
                                    />
                                )}

                                <DisplayViewSelector
                                    vm={this.props.vm}
                                    className="d-inline-block ms-1"
                                    link={this.appStore.isMobile}
                                />

                                {(this.props.vm.viewType == DisplayViewTypes.TableLarge ||
                                    this.props.vm.viewType == DisplayViewTypes.TableSmall ||
                                    this.props.vm.viewType == DisplayViewTypes.TableExtraSmall ||
                                    this.props.vm.viewType == DisplayViewTypes.Aggregate) && (
                                    <DisplaysTableSettings vm={this.props.vm} className="d-inline-block ms-1" />
                                )}
                            </div>

                            {this.appStore.isMobile && (
                                <a onClick={this.toggleExtraFilters} className={this.filterToggleClass}>
                                    <BsFillFunnelFill />
                                </a>
                            )}
                        </div>
                    }
                />
            </SubTopBar>
        );
    }

    @bind
    @action
    private toggleExtraFilters() {
        this.showExtraFilters = !this.showExtraFilters;
    }

    @bind
    @action
    private onColumnSearchChange(event: React.FormEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;
        this.columnSearchString = target.value;
        this.searchCells = this.headerCells.filter(o => {
            const cellName = o.id;
            return deepStringFind(cellName, this.columnSearchString);
        });
        if (this.searchCells.length > 0) {
            this.cellSearchIndex = 0;
            this.selectedCell = this.searchCells[0];
            this.selectedCell.scrollIntoView({ inline: 'end' });
            this.props.vm.searchedColumn = this.selectedCell.id;
        }
        if (this.searchCells.length == this.headerCells.length) {
            this.props.vm.searchedColumn = "";
        }
    }

    @bind
    @action
    private nextSearchCell(event: React.FormEvent<HTMLButtonElement>) {
        if (this.cellSearchIndex + 1 < this.searchCells.length) {
            this.cellSearchIndex = this.cellSearchIndex + 1;
        } else {
            this.cellSearchIndex = 0;
        }

        this.selectedCell = this.searchCells[this.cellSearchIndex];
        this.selectedCell.scrollIntoView({ inline: 'end' });
        this.props.vm.searchedColumn = this.selectedCell.id;
    }

    @bind
    @action
    private previousSearchCell(event: React.FormEvent<HTMLButtonElement>) {
        if (this.cellSearchIndex == 0) {
            this.cellSearchIndex = this.searchCells.length - 1;
        } else {
            this.cellSearchIndex = this.cellSearchIndex - 1;
        }

        this.selectedCell = this.searchCells[this.cellSearchIndex];
        this.selectedCell.scrollIntoView({ inline: 'end' });
        this.props.vm.searchedColumn = this.selectedCell.id;
    }

    @bind
    @action
    private onColumnReset() {
        if (this.props.vm.viewType == DisplayViewTypes.Aggregate) {
            this.accountStore.storage.displayAggregateColumns = defaultAggregateColumns;
            this.props.vm.onColumnChange(this.accountStore.storage.displayAggregateColumns);
        } else {
            this.props.vm.clearAllColumns();
            this.accountStore.storage.displayTableColumns = defaultTableColumns;
            this.props.vm.onColumnChange(this.accountStore.storage.displayTableColumns);
        }
    }
    
    @computed
    private get hideInput() {
        const { hideInput } = this.props;

        if (hideInput) return true;
        else if (!this.appStore.isMobile) return false;
        else return this.showExtraFilters;
    }

    @computed
    private get filterToggleClass() {
        let toggleClass = 'btn btn-secondary btn-sm ms-1';
        if (this.showExtraFilters) toggleClass = toggleClass + ' active';
        return toggleClass;
    }

    @computed
    private get headerCells() {
        let cells: HTMLTableHeaderCellElement[] = [];
        if (this.props.vm.headerRowRef) {
            const rowRef = this.props.vm.headerRowRef;
            cells = Array.from(rowRef.cells);
        }

        return cells;
    }

    @observable
    private columnSearchString: string = '';
    @observable
    private showExtraFilters: boolean = false;
    @observable
    private searchCells: HTMLTableCellElement[] = [];
    @observable
    private selectedCell: HTMLTableCellElement | undefined = undefined;
    @observable
    private cellSearchIndex: number = 0;
    @inject
    private accountStore!: AccountStore;
    @inject
    private appStore!: AppStore;
}
